import { useEffect, useRef, useState } from 'react';

import { GAME_MODE, SingSetup, SongPreview } from '~/interfaces';
import { Calibration } from '~/modules/calibration/calibration';
import { Menu } from '~/modules/elements/akui/menu';
import { useBackground } from '~/modules/elements/background-context';
import Modal from '~/modules/elements/modal';
import { VideoState } from '~/modules/elements/video-player/index';
import GameState from '~/modules/game-engine/game-state/game-state';
import { calculateNoteStats } from '~/modules/game-engine/game-state/helpers/calculate-note-stats';
import events from '~/modules/game-events/game-events';
import useBlockScroll from '~/modules/hooks/use-block-scroll';
import useFullscreen from '~/modules/hooks/use-fullscreen';
import useViewportSize from '~/modules/hooks/use-viewport-size';
import { PlayerNumber } from '~/modules/players/player-number';
import PlayersManager from '~/modules/players/players-manager';
import RemoteMicServer from '~/modules/remote-mic/network/server';
import useSong from '~/modules/songs/hooks/use-song';
import { CalibrationIntro } from '~/routes/game/singing/calibration-intro';
import { ownsSection } from '~/routes/game/singing/game-overlay/helpers/pass-the-mic-turn';
import PracticeOverlay from '~/routes/game/singing/practice-overlay';
import WaitForReadiness from '~/routes/game/singing/wait-for-readiness';
import LayoutGame from '~/routes/layout-game';
import { IsCalibratedSetting, useSettingValue } from '~/routes/settings/settings-state';

import Player, { PlayerRef } from './player';
import PostGame from './post-game/post-game';

interface Props {
  singSetup: SingSetup;
  songPreview: SongPreview;
  returnToSongSelection: () => void;
  restartSong: () => void;
}
function Singing({ songPreview, singSetup, returnToSongSelection, restartSong }: Props) {
  useFullscreen();
  useBlockScroll();
  const player = useRef<PlayerRef | null>(null);
  const song = useSong(songPreview.id, {
    sourceType: songPreview.sourceType,
    sharedSongId: songPreview.sharedSongId,
  });
  const [isCalibrated, setIsCalibrated] = useSettingValue(IsCalibratedSetting);

  const { width, height } = useViewportSize();
  const [isEnded, setIsEnded] = useState(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState(true);
  const [showCalibrationIntro, setShowCalibrationIntro] = useState(true);
  const [playerState, setPlayerState] = useState(VideoState.UNSTARTED);

  const [isTransitionTimeout, setIsTransitionTimeout] = useState(false);
  // The pause menu lives inside `Player`, and the readiness screen is its sibling - this is what
  // carries "the menu is open" from the one to the other.
  const [isPauseMenuOpen, setIsPauseMenuOpen] = useState(false);

  useBackground(!isTransitionTimeout);

  // What the phones are told is playing. Tied to this screen's own life rather than to the
  // `songStarted`/`songEnded` pair, because only one of those two is guaranteed: a song left by
  // navigating away, or by reloading the host, never ends - and a phone that was told a song started
  // and never told it stopped sits on the singing screen for ever with its other two tabs locked.
  // Unmounting covers every way out, including the ones nobody thought of.
  useEffect(() => {
    if (isEnded) {
      RemoteMicServer.publish('current-song', undefined);
      return;
    }

    RemoteMicServer.publish('current-song', {
      title: songPreview.title,
      artist: songPreview.artist,
      // `isTransitionTimeout` is set by the readiness screen handing over, which is the same tick
      // the player is told to play - so it is the moment the song starts rather than the moment
      // this screen mounted.
      started: isTransitionTimeout,
    });

    return () => RemoteMicServer.publish('current-song', undefined);
  }, [isEnded, isTransitionTimeout, songPreview.title, songPreview.artist]);

  const showCalibration = !isCalibrated;
  useEffect(() => {
    if (
      !showCalibration &&
      isOverlayVisible &&
      song.data &&
      (isTransitionTimeout || playerState !== VideoState.UNSTARTED)
    ) {
      setIsOverlayVisible(false);
    }
  }, [song.data, isTransitionTimeout, playerState, isOverlayVisible, showCalibration]);

  // Nothing was recorded, so there is nothing to show afterwards - straight back to the songs.
  if (isEnded && song.data && singSetup.practice) {
    returnToSongSelection();

    return null;
  }

  if (isEnded && song.data) {
    return (
      <PostGame
        width={width}
        height={height}
        song={song.data}
        onClickSongSelection={returnToSongSelection}
        singSetup={singSetup}
      />
    );
  } else {
    const waitingForReadiness = !showCalibration && !isTransitionTimeout;

    return (
      <LayoutGame>
        <div className="relative">
          <div
            className={`pointer-events-none fixed inset-0 z-10 flex h-full w-full flex-col items-stretch px-10 py-10 transition-opacity duration-500 [view-transition-name:song-preview] ${
              isOverlayVisible ? 'opacity-100' : 'opacity-0'
            }`}
            data-test="background-container">
            <div
              className="absolute inset-0 scale-105 bg-black bg-cover bg-center blur-[10px]"
              style={{
                backgroundImage: `url('https://i3.ytimg.com/vi/${songPreview.video}/hqdefault.jpg')`,
                width: `${width}px`,
                height: `${height}px`,
              }}
            />
            {/* Hidden rather than dropped while the readiness screen is up: that screen names the
                song itself, in the same corner and in larger type, so both at once is the same two
                lines drawn twice through a 72% scrim. Kept in the DOM so what the song is stays
                readable to anything asking. */}
            <span
              className={`typography text-2xl [view-transition-name:song-preview-artist] 2xl:text-3xl ${waitingForReadiness ? 'invisible' : ''}`}
              data-test="song-artist">
              {songPreview.artist}
            </span>
            <span
              className={`typography text-active text-3xl [view-transition-name:song-preview-title] 2xl:text-5xl ${waitingForReadiness ? 'invisible' : ''}`}
              data-test="song-title">
              {songPreview.title}
            </span>
            <Modal open={showCalibration}>
              {showCalibration && (
                <Menu modal>
                  {showCalibrationIntro ? (
                    <CalibrationIntro onContinue={() => setShowCalibrationIntro(false)} />
                  ) : (
                    <Calibration onSave={() => setIsCalibrated(true)} />
                  )}
                </Menu>
              )}
            </Modal>
            {!showCalibration && (
              <WaitForReadiness
                title={songPreview.title}
                artist={songPreview.artist}
                paused={isPauseMenuOpen}
                onFinish={() => {
                  setIsTransitionTimeout(true);
                  player.current?.play();
                }}
              />
            )}
          </div>
          {song.data && !showCalibration && (
            <Player
              pauseMenu
              onPauseMenuVisibilityChange={setIsPauseMenuOpen}
              hintsAsStrip={waitingForReadiness}
              ref={player}
              onStatusChange={setPlayerState}
              song={song.data}
              width={width}
              height={height}
              autoplay={false}
              onSongEnd={() => {
                // Practice runs are not plays: no dispatch, so every listener that records one
                // (stats, profiles, achievements, history, the camera) is skipped at the source.
                if (singSetup.practice) {
                  setIsEnded(true);

                  return;
                }

                // Measured here, while the sung notes are still in `GameState` - once the song
                // is over there is nothing left to count them from.
                const notesFor = (playerNumber: PlayerNumber) => {
                  const playerState = GameState.getPlayer(playerNumber);

                  return playerState
                    ? calculateNoteStats(
                        playerState.getPlayerNotes(),
                        playerState.getTrack(),
                        GameState.getTolerance(),
                        // Marked on the sections that were theirs to sing, so pass the mic does not
                        // count a partner's lines against them.
                        { ownsSection: (section) => ownsSection(playerNumber, section) },
                      )
                    : undefined;
                };

                const scores =
                  GameState.getSingSetup()?.mode === GAME_MODE.CO_OP
                    ? [
                        {
                          name: PlayersManager.getPlayers()
                            .map((player) => player.getName())
                            .join(', '),
                          score: GameState.getPlayerScore(0),
                        },
                      ]
                    : PlayersManager.getPlayers().map((player) => ({
                        name: player.getName(),
                        score: GameState.getPlayerScore(player.number),
                        notes: notesFor(player.number),
                      }));
                const progress = GameState.getSongCompletionProgress();
                events.songEnded.dispatch(song.data!, singSetup, scores, progress);
                setIsEnded(true);
              }}
              singSetup={singSetup}
              restartSong={restartSong}
            />
          )}
          {singSetup.practice && song.data && (
            <PracticeOverlay playerRef={player} song={song.data} onExit={returnToSongSelection} />
          )}
        </div>
      </LayoutGame>
    );
  }
}

export default Singing;
